/* ══════════════════════════════════════════════════════════════
   upstox.ts — Upstox API Client
   Portfolio + order tracking
   ══════════════════════════════════════════════════════════════ */

export interface UpstoxConfig {
    apiKey: string;
    accessToken: string;
    baseUrl: string;
}

export interface UpstoxHolding {
    symbol: string;
    exchange: string;
    quantity: number;
    averagePrice: number;
    lastPrice: number;
    pnl: number;
    dayChange: number;
    investedValue: number;
    currentValue: number;
}

export interface UpstoxOrder {
    orderId: string;
    symbol: string;
    orderType: string;
    side: 'BUY' | 'SELL';
    quantity: number;
    price: number;
    status: string;
    timestamp: string;
}

export interface UpstoxProfile {
    userId: string;
    userName: string;
    email: string;
    broker: 'upstox';
}

const DEFAULT_BASE_URL = 'https://api.upstox.com/v2';

function getConfig(): UpstoxConfig {
    return {
        apiKey: localStorage.getItem('imm-upstox-api-key') || '',
        accessToken: localStorage.getItem('imm-upstox-access-token') || '',
        baseUrl: DEFAULT_BASE_URL,
    };
}

async function upstoxRequest<T>(endpoint: string): Promise<T> {
    const config = getConfig();
    if (!config.accessToken) {
        throw new Error('Upstox access token not set.');
    }

    const response = await fetch(`${config.baseUrl}${endpoint}`, {
        headers: {
            'Authorization': `Bearer ${config.accessToken}`,
            'Accept': 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Upstox API error: ${response.status}`);
    }

    const json = await response.json();
    return json.data;
}

// ─── Portfolio ───

export async function getHoldings(): Promise<UpstoxHolding[]> {
    try {
        return await upstoxRequest<UpstoxHolding[]>('/portfolio/long-term-holdings');
    } catch {
        return [];
    }
}

export async function getOrders(): Promise<UpstoxOrder[]> {
    try {
        return await upstoxRequest<UpstoxOrder[]>('/order/retrieve-all');
    } catch {
        return [];
    }
}

export async function getProfile(): Promise<UpstoxProfile> {
    try {
        return await upstoxRequest<UpstoxProfile>('/user/profile');
    } catch {
        return { userId: '', userName: 'Disconnected', email: '', broker: 'upstox' };
    }
}

// ─── Auth ───

export function getLoginUrl(): string {
    const config = getConfig();
    return `https://api.upstox.com/v2/login/authorization/dialog?response_type=code&client_id=${config.apiKey}&redirect_uri=${encodeURIComponent(window.location.origin + '/callback/upstox')}`;
}

export function isConnected(): boolean {
    return !!localStorage.getItem('imm-upstox-access-token');
}

export function disconnect(): void {
    localStorage.removeItem('imm-upstox-access-token');
}
